import { Link, Navigate, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslations } from 'use-intl';

import { PageHeader } from '@/components/dashboard/page-header';
import { OfferEditForm } from '@/components/offer/offer-edit-form';
import { trpc } from '@/lib/trpc/client';

import type { OfferLanguage } from '@devizy/shared';

export const Route = createFileRoute('/_app/offers/$id/edit')({
  component: OfferEditPage,
});

function OfferEditPage(): React.ReactElement {
  const { id } = Route.useParams();
  const t = useTranslations('offer.modal');
  const offerQuery = trpc.offer.byId.useQuery({ offerId: id });

  if (offerQuery.isLoading) {
    return <p className="text-muted-foreground text-sm">Chargement…</p>;
  }
  if (offerQuery.isError || !offerQuery.data) {
    return (
      <div className="border-border bg-card rounded-2xl border p-8 text-center">
        <h1 className="font-display text-base">Offre introuvable</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Cette offre n&apos;existe pas ou ne vous appartient pas.
        </p>
        <Link
          to="/offers"
          className="text-primary mt-4 inline-block text-sm font-medium hover:underline"
        >
          Retour aux offres
        </Link>
      </div>
    );
  }

  const offer = offerQuery.data;

  if (offer.status === 'DRAFT') {
    return <Navigate to="/offers/new" search={{ draftId: offer.id }} replace />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Modifier l'offre" subtitle={offer.title ?? undefined} />
      <OfferEditForm
        offerId={offer.id}
        initialTitle={offer.title ?? ''}
        initialDescription={offer.description ?? ''}
        initialBudgetAmount={offer.budgetAmount}
        initialExpectations={offer.expectations}
        status={offer.status}
        currentVersion={offer.currentVersion}
        primaryLanguage={offer.primaryLanguage as OfferLanguage}
        secondaryLanguages={(offer.secondaryLanguages as OfferLanguage[] | null) ?? []}
        modalMessages={{
          title: t('title'),
          aiSlotTitle: t('aiSlotTitle'),
          aiSlotBody: t('aiSlotBody'),
          save: t('save'),
          discard: t('discard'),
          addParent: t('addParent'),
          addChild: t('addChild'),
          remove: t('remove'),
          removeConfirmTitle: t('removeConfirmTitle'),
          removeConfirmBody: t('removeConfirmBody'),
          discardConfirmTitle: t('discardConfirmTitle'),
          discardConfirmBody: t('discardConfirmBody'),
          viewDiff: t('viewDiff'),
          hideDiff: t('hideDiff'),
          manuallyEdited: t('manuallyEdited'),
          manuallyEditedHint: t('manuallyEditedHint'),
          modulePlaceholder: t('modulePlaceholder'),
          childPlaceholder: t('childPlaceholder'),
          descriptionPlaceholder: t('descriptionPlaceholder'),
          offerTitlePlaceholder: t('offerTitlePlaceholder'),
        }}
      />
    </div>
  );
}
